import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CustomThemeProvider } from './context/ThemeContext';
import { Box, useMediaQuery, Skeleton, Stack } from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';

import Login from './pages/Login';
import Sidebar from './components/Navigation';
import MainNavbar from './components/MainNavbar';
import MobileBottomNav from './components/MobileBottomNav';
import { supabase } from './config/supabaseClient';
import SystemLaunch from './components/SystemLaunch';
import AgbPopup from './components/AgbPopup';
// import DataImportWizard from "./pages/admin/DataImportWizard";

// Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Allgemein = lazy(() => import('./pages/Allgemein'));
const ComplianceQM = lazy(() => import('./pages/ComplianceQM'));
const Recht = lazy(() => import('./pages/Recht'));
const Benutzerverwaltung = lazy(() => import('./pages/Benutzerverwaltung'));
const HrManagement = lazy(() => import('./pages/HrManagement'));
const Produktverwaltung2 = lazy(() => import('./pages/Produktverwaltung2'));
const Kundenverwaltung = lazy(() => import('./pages/Kundenverwaltung'));
const KundenDetail = lazy(() => import('./pages/KundenDetail'));
const Entwicklung = lazy(() => import('./pages/Entwicklung'));
const Preisverwaltung = lazy(() => import('./pages/Preisverwaltung'));
const PreisverwaltungAlt = lazy(() => import('./pages/PreisverwaltungAlt'));
const BundleVerwaltung = lazy(() => import('./pages/BundleVerwaltung'));
const SystemHandbuch = lazy(() => import('./pages/SystemHandbuch'));
const Abteilungsverwaltung = lazy(() => import('./pages/Abteilungsverwaltung'));
const VerwaltungRechteRollen = lazy(() => import('./pages/VerwaltungRechteRollen'));
const Ausgabenverwaltung = lazy(() => import('./pages/Ausgabenverwaltung'));
const Inhaltverwaltung = lazy(() => import('./pages/Inhaltverwaltung'));
const Einkaufverwaltung = lazy(() => import('./pages/Einkaufverwaltung'));
const Lagerverwaltung = lazy(() => import('./pages/Lagerverwaltung'));
const Dokumentenverwaltung = lazy(() => import('./pages/Dokumentenverwaltung'));
const AuftragUebersicht = lazy(() => import('./pages/AuftragUebersicht'));
const AuftragsmanagementOMS = lazy(() => import('./pages/OMS/AuftragsmanagementOMS'));
const AboLaufDashboard = lazy(() => import('./pages/OMS/komponenten/AboLaufDashboard'));
const B2BPreislisten = lazy(() => import('./pages/CRM/B2BPreislisten'));
const Angebote = lazy(() => import('./pages/CRM/Angebote'));
const Workflows = lazy(() => import('./pages/Workflows'));
const IT = lazy(() => import('./pages/IT'));
const Datev = lazy(() => import('./pages/Datev'));
const Woocommerce = lazy(() => import('./pages/Woocommerce'));
const DashboardOverview = lazy(() => import('./pages/DashboardOverview'));
const Unternehmensdaten = lazy(() => import('./pages/Unternehmensdaten'));
const DokumentenTemplates = lazy(() => import('./pages/DokumentenTemplates'));
const Qualitaetsmanagement = lazy(() => import('./pages/Qualitaetsmanagement'));
const Verpackungsverwaltung = lazy(() => import('./pages/Verpackungsverwaltung'));

const Kapazitaetsplanung = lazy(() => import('./pages/Kapazitaetsplanung'));
const KatalogZentrale = lazy(() => import('./pages/Stammdaten/KatalogZentrale'));
const ProduktionsSetupZentrale = lazy(() => import('./pages/Stammdaten/ProduktionsSetupZentrale'));
const Arbeitsplatzvorbereitung = lazy(() => import('./pages/Arbeitsplatzvorbereitung'));
const Arbeitsplaetze = lazy(() => import('./pages/Arbeitsplaetze'));
const WorkflowsZentrale = lazy(() => import('./pages/WorkflowsZentrale'));

const BIProduktion = lazy(() => import('./pages/BI/BIProduktion'));
const BIFinanzen = lazy(() => import('./pages/BI/BIFinanzen'));
const BIScm = lazy(() => import('./pages/BI/BIScm'));
const BISales = lazy(() => import('./pages/BI/BISales'));
const ProzessMonitor = lazy(() => import('./pages/BI/ProzessMonitor'));
const DashboardBuilder = lazy(() => import('./pages/BI/DashboardBuilder'));
const MeineAufgaben = lazy(() => import('./pages/Workspace/MeineAufgaben'));
const MESZentrale = lazy(() => import('./pages/MES/MESZentrale'));
const ProductionFlows = lazy(() => import('./pages/ProductionFlows'));
const Versand = lazy(() => import('./pages/Versand'));
const Debitoren = lazy(() => import('./pages/Debitoren/Rechnungen'));
const Ausgaben = lazy(() => import('./pages/Ausgaben'));
const BankKasse = lazy(() => import('./pages/BankKasse'));
const Arbeitszeiten = lazy(() => import('./pages/Arbeitszeiten'));
const Markt = lazy(() => import('./pages/Markt/MarktWrapper'));
const DashboardLagerbestand = lazy(() => import('./pages/DashboardLagerbestand'));
const DashboardAnalyse = lazy(() => import('./pages/DashboardAnalyse'));
const DashboardFinanzenAnalyse = lazy(() => import('./pages/DashboardFinanzenAnalyse'));
const KalenderKanban = lazy(() => import('./pages/KalenderKanban'));
const ProjektmanagementDashboard = lazy(() => import('./pages/ProjektmanagementDashboard'));
const Projekte = lazy(() => import('./pages/Projekte'));
const Timeline = lazy(() => import('./pages/Timeline'));
const Whiteboard = lazy(() => import('./pages/Whiteboard'));
const KalenderScheduler = lazy(() => import('./pages/KalenderScheduler'));
const ChatRoom = lazy(() => import('./pages/ChatRoom'));
const AIBot = lazy(() => import('./pages/AIBot'));
const LegalCompliance = lazy(() => import('./pages/LegalCompliance'));
const Checklisten = lazy(() => import('./pages/Checklisten'));
const AdministrationDeployment = lazy(() => import('./pages/AdministrationDeployment'));
const AdministrationFreischaltung = lazy(() => import('./pages/AdministrationFreischaltung'));
const AdministrationAnforderungen = lazy(() => import('./pages/AdministrationAnforderungen'));
const AdministrationAuditLogs = lazy(() => import('./pages/AdministrationAuditLogs'));
const AdministrationInfo = lazy(() => import('./pages/AdministrationInfo'));
const Integrationen = lazy(() => import('./pages/Integrationen'));
const DataImportWizard = lazy(() => import('./pages/admin/DataImport/DataImportWizard')
);



const PrivateRoute = ({ children }) => {
  const isDev = process.env.NODE_ENV === 'development';
  const isAuthenticated = isDev || localStorage.getItem('isAuthenticated') === 'true';
  const { search, hash } = window.location;

  // If we have an invite/recovery token, we MUST preserve it in the redirect
  return isAuthenticated ? children : <Navigate to={`/login${search}${hash}`} />;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');
  const isChat = location.pathname.includes('/nachrichten/') || location.pathname.includes('entwicklung');

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100vh', background: '#f5f6fa', position: 'relative', overflow: 'hidden' }}>
      <AgbPopup />
      {!isMobile && <Sidebar />}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!isMobile && <MainNavbar />}
        <Box sx={{
          flex: 1, minHeight: 0,
          p: isChat ? 0 : { xs: 1, md: 1.5 },
          overflow: 'hidden', display: 'flex', flexDirection: 'column'
        }}>
          <Suspense fallback={
            <Box sx={{ p: 2, height: '100%' }}>
              <Skeleton variant="rectangular" width="40%" height={32} sx={{ mb: 3, borderRadius: 2 }} />
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={100} sx={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" height={100} sx={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" height={100} sx={{ borderRadius: 4 }} />
              </Stack>
            </Box>
          }>
            {children}
          </Suspense>
        </Box>
        {isMobile && <MobileBottomNav />}
      </Box>
    </Box>
  );
};


const AuthHandler = ({ onSessionHandled }) => {
  const navigate = useNavigate();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(session.user));
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
      }
      onSessionHandled(true);
    });
    return () => subscription.unsubscribe();
  }, [navigate, onSessionHandled]);
  return null;
};

const App = () => {
  const [isInitializing, setIsInitializing] = useState(false);

  const handleLaunchComplete = () => {
    sessionStorage.setItem('launchShown', 'true');
    setIsInitializing(false);
  };

  return (
    <CustomThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthHandler onSessionHandled={() => { }} />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/registration" element={<Login />} />

            {/* 1) Allgemein */}
            <Route path="/allgemein/dokumente" element={<PrivateRoute><Layout><Dokumentenverwaltung /></Layout></PrivateRoute>} />
            <Route path="/allgemein/workflows" element={<PrivateRoute><Layout><Workflows /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/firma" element={<PrivateRoute><Layout><Unternehmensdaten /></Layout></PrivateRoute>} />
            <Route path="/allgemein/firma" element={<Navigate to="/verwaltung/firma" replace />} />
            <Route path="/organisation/compliance" element={<PrivateRoute><Layout><ComplianceQM /></Layout></PrivateRoute>} />
            <Route path="/allgemein/recht" element={<PrivateRoute><Layout><Recht /></Layout></PrivateRoute>} />
            <Route path="/allgemein/templates" element={<PrivateRoute><Layout><DokumentenTemplates /></Layout></PrivateRoute>} />
            
            <Route path="/einstellungen" element={<Navigate to="/verwaltung/firma" replace />} />

            {/* 2) Verwaltung */}
            <Route path="/verwaltung/mitarbeiter" element={<PrivateRoute><Layout><Benutzerverwaltung /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/rechte-rollen" element={<PrivateRoute><Layout><VerwaltungRechteRollen /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/abteilungen" element={<PrivateRoute><Layout><Abteilungsverwaltung /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/kunden" element={<PrivateRoute><Layout><Kundenverwaltung /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/kunden/:id" element={<PrivateRoute><Layout><KundenDetail /></Layout></PrivateRoute>} />
            <Route path="/verwaltung/personal" element={<PrivateRoute><Layout><HrManagement /></Layout></PrivateRoute>} />

            {/* 3) Sortiment */}
            <Route path="/sortiment/einkauf" element={<PrivateRoute><Layout><Einkaufverwaltung /></Layout></PrivateRoute>} />
            <Route path="/sortiment/preisregeln_alt" element={<PrivateRoute><Layout><PreisverwaltungAlt /></Layout></PrivateRoute>} />

            {/* 4) CRM & Sales */}
            <Route path="/crm-sales/debitoren" element={<PrivateRoute><Layout><Debitoren /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/auftraege" element={<PrivateRoute><Layout><AuftragUebersicht /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/preislisten" element={<PrivateRoute><Layout><B2BPreislisten /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/angebote" element={<PrivateRoute><Layout><Angebote /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/abo-lauf" element={<PrivateRoute><Layout><AboLaufDashboard /></Layout></PrivateRoute>} />
              <Route path="/crm-sales/oms" element={<PrivateRoute><Layout><AuftragsmanagementOMS /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/maerkte" element={<PrivateRoute><Layout><Markt /></Layout></PrivateRoute>} />
            <Route path="/crm-sales/woocommerce" element={<PrivateRoute><Layout><Woocommerce /></Layout></PrivateRoute>} />

            {/* 5) Produktion & Logistik */}
            <Route path="/produktion/produktion" element={<Navigate to="/produktion/mes-fertigung" replace />} />
            <Route path="/bi/produktion" element={<PrivateRoute><Layout><BIProduktion /></Layout></PrivateRoute>} />
            <Route path="/bi/finanzen" element={<PrivateRoute><Layout><BIFinanzen /></Layout></PrivateRoute>} />
            <Route path="/bi/scm" element={<PrivateRoute><Layout><BIScm /></Layout></PrivateRoute>} />
            <Route path="/bi/sales" element={<PrivateRoute><Layout><BISales /></Layout></PrivateRoute>} />
            <Route path="/bi/prozess-monitor" element={<PrivateRoute><Layout><ProzessMonitor /></Layout></PrivateRoute>} />
            <Route path="/bi/dashboard-builder" element={<PrivateRoute><Layout><DashboardBuilder /></Layout></PrivateRoute>} />
            <Route path="/workspace/meine-aufgaben" element={<PrivateRoute><Layout><MeineAufgaben /></Layout></PrivateRoute>} />
            <Route path="/produktion/mes-fertigung" element={<PrivateRoute><Layout><MESZentrale /></Layout></PrivateRoute>} />
            <Route path="/produktion/kapazitaetsplanung" element={<PrivateRoute><Layout><Kapazitaetsplanung /></Layout></PrivateRoute>} />
            <Route path="/produktion/flows" element={<PrivateRoute><Layout><ProductionFlows /></Layout></PrivateRoute>} />
            <Route path="/produktion/qm" element={<PrivateRoute><Layout><Qualitaetsmanagement /></Layout></PrivateRoute>} />
            <Route path="/stammdaten/katalog" element={<PrivateRoute><Layout><KatalogZentrale /></Layout></PrivateRoute>} />
            <Route path="/stammdaten/produktion" element={<PrivateRoute><Layout><ProduktionsSetupZentrale /></Layout></PrivateRoute>} />
            <Route path="/logistik-lager/versand" element={<PrivateRoute><Layout><Versand /></Layout></PrivateRoute>} />
            <Route path="/logistik-lager/inventory" element={<PrivateRoute><Layout><Lagerverwaltung /></Layout></PrivateRoute>} />
            <Route path="/logistik-lager/bestand" element={<PrivateRoute><Layout><DashboardLagerbestand /></Layout></PrivateRoute>} />
            <Route path="/bi-analytics" element={<PrivateRoute><Layout><DashboardAnalyse /></Layout></PrivateRoute>} />
            <Route path="/produktion/arbeitszeiten" element={<PrivateRoute><Layout><Arbeitszeiten /></Layout></PrivateRoute>} />

            {/* 6) Finanzen & Controlling */}
            <Route path="/finanzen-controlling/overview" element={<PrivateRoute><Layout><DashboardOverview /></Layout></PrivateRoute>} />
            <Route path="/finanzen-controlling/ausgaben" element={<PrivateRoute><Layout><Ausgaben /></Layout></PrivateRoute>} />
            <Route path="/finanzen-controlling/bank" element={<PrivateRoute><Layout><BankKasse /></Layout></PrivateRoute>} />
            <Route path="/finanzen-controlling/analyse" element={<PrivateRoute><Layout><DashboardFinanzenAnalyse /></Layout></PrivateRoute>} />
            <Route path="/finanzen-controlling/datev" element={<PrivateRoute><Layout><Datev /></Layout></PrivateRoute>} />

            {/* 7) Projektmanagement */}
            <Route path="/projektmanagement/dashboard" element={<PrivateRoute><Layout><ProjektmanagementDashboard /></Layout></PrivateRoute>} />
            <Route path="/projektmanagement/projekte" element={<PrivateRoute><Layout><Projekte /></Layout></PrivateRoute>} />
            <Route path="/projektmanagement/kanban" element={<PrivateRoute><Layout><KalenderKanban /></Layout></PrivateRoute>} />
            <Route path="/projektmanagement/timeline" element={<PrivateRoute><Layout><Timeline /></Layout></PrivateRoute>} />
            <Route path="/projektmanagement/kalender" element={<PrivateRoute><Layout><KalenderScheduler /></Layout></PrivateRoute>} />

            {/* 8) Whiteboard */}
            <Route path="/whiteboard" element={<PrivateRoute><Layout><Whiteboard /></Layout></PrivateRoute>} />
            <Route path="/checklisten" element={<PrivateRoute><Layout><Checklisten /></Layout></PrivateRoute>} />

            {/* 9) Nachrichten */}
            <Route path="/nachrichten/chatroom" element={<PrivateRoute><Layout><ChatRoom /></Layout></PrivateRoute>} />
            <Route path="/nachrichten/ai-bot" element={<PrivateRoute><Layout><AIBot /></Layout></PrivateRoute>} />

            {/* 10) Administration */}
            <Route path="/administration/deployment" element={<PrivateRoute><Layout><AdministrationDeployment /></Layout></PrivateRoute>} />
            <Route path="/administration/freischaltung" element={<PrivateRoute><Layout><AdministrationFreischaltung /></Layout></PrivateRoute>} />
            <Route path="/administration/anforderungen" element={<PrivateRoute><Layout><AdministrationAnforderungen /></Layout></PrivateRoute>} />
            <Route path="/administration/audit-logs" element={<PrivateRoute><Layout><AdministrationAuditLogs /></Layout></PrivateRoute>} />
            <Route path="/administration/it" element={<PrivateRoute><Layout><IT /></Layout></PrivateRoute>} />
            <Route path="/administration/integrationen" element={<PrivateRoute><Layout><Integrationen /></Layout></PrivateRoute>} />
            <Route path="/administration/datenimport" element={<PrivateRoute><Layout><DataImportWizard /></Layout></PrivateRoute>} />
            <Route path="/administration/info" element={<PrivateRoute><Layout><AdministrationInfo /></Layout></PrivateRoute>} />

            <Route path="/system-handbuch" element={<PrivateRoute><Layout><SystemHandbuch /></Layout></PrivateRoute>} />

            {/* Standard Redirects */}
            <Route path="/produktion-logistik/produktion" element={<Navigate to="/produktion/mes-fertigung" replace />} />
            <Route path="/produktion-logistik/production-flows" element={<Navigate to="/produktion/flows" replace />} />
            <Route path="/produktion-logistik/arbeitszeiten" element={<Navigate to="/produktion/arbeitszeiten" replace />} />
            <Route path="/produktion-logistik/versand" element={<Navigate to="/logistik-lager/versand" replace />} />
            <Route path="/sortiment/lagerverwaltung" element={<Navigate to="/logistik-lager/inventory" replace />} />
            <Route path="/produktion-logistik/lagerbestand" element={<Navigate to="/logistik-lager/bestand" replace />} />

            <Route path="/" element={<PrivateRoute><Layout><LandingPage /></Layout></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </LocalizationProvider>
    </CustomThemeProvider>
  );
};

export default App;